import { type ComponentProps, memo, type ReactNode, useMemo } from 'react';

import { EllipsisIcon, EllipsisVerticalIcon, SettingsIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { NotificationsFilters, type NotificationsFiltersProps } from './NotificationsFilters';

export type NotificationsHeaderProps = ComponentProps<'header'> &
  NotificationsFiltersProps & {
    leading?: ReactNode;
    options?: DropdownMenuOption[];
    onMarkAllAsRead?: () => void;
    onSettingsClick?: () => void;
    isUI2?: boolean;
  };

export const NotificationsHeader = memo(
  ({
    filters,
    leading,
    onFilterClick,
    onMarkAllAsRead,
    onSettingsClick,
    options,
    isUI2,
    ...restOfProps
  }: NotificationsHeaderProps) => {
    const dropdownOptions = useMemo(
      () => [
        ...(onMarkAllAsRead
          ? [
              {
                id: 'mark-all-read',
                label: 'Mark all as read',
                onSelect: onMarkAllAsRead,
                // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
                type: 'item' as const,
              },
            ]
          : []),
        ...(onSettingsClick
          ? [
              {
                id: 'settings',
                label: 'Settings',
                onSelect: onSettingsClick,
                // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
                type: 'item' as const,
              },
            ]
          : []),
      ],
      [onMarkAllAsRead, onSettingsClick],
    );

    return (
      <header
        className="sticky top-0 ui2026:z-10 z-0 shrink-0 border-b border-b-gray-4 bg-gray-1 pt-4 ui2026:pt-3"
        data-testid="NOTIFICATIONS_HEADER"
        {...restOfProps}
      >
        <div className="flex items-center justify-between px-4">
          {leading ? (
            <div className="flex items-center gap-2">
              {leading}
              <h3 className="font-semibold text-14 text-gray-12 ui2026:text-16">Notifications</h3>
            </div>
          ) : (
            <h3 className="font-semibold text-14 text-gray-12 ui2026:text-16">Notifications</h3>
          )}

          <div className="flex shrink-0 items-center gap-2">
            {isUI2 ? (
              <DropdownMenu
                align="end"
                trigger={
                  <IconButton appearance="ghost" color="gray" label="Settings">
                    <EllipsisIcon size="sm" />
                  </IconButton>
                }
              >
                {renderDropdownItems({
                  options: dropdownOptions,
                })}
              </DropdownMenu>
            ) : (
              <>
                {!!onSettingsClick && (
                  <IconButton appearance="ghost" color="gray" label="Notification settings" onClick={onSettingsClick}>
                    <SettingsIcon size="sm" />
                  </IconButton>
                )}

                {!!options && (
                  <DropdownMenu
                    align="end"
                    trigger={
                      <IconButton appearance="ghost" color="gray" label="Settings">
                        <EllipsisVerticalIcon size="sm" />
                      </IconButton>
                    }
                  >
                    {renderDropdownItems({ options })}
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex min-h-[40px] items-center justify-between pr-4">
          <NotificationsFilters filters={filters} onFilterClick={onFilterClick} />

          {!isUI2 && (
            <Button appearance="ghost" className="shrink-0" color="gray" onClick={onMarkAllAsRead} size="small">
              Mark all as read
            </Button>
          )}
        </div>
      </header>
    );
  },
);

NotificationsHeader.displayName = 'NotificationsHeader';
